'use client'

import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/cn'

const AccordionStepper = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Root
    ref={ref}
    className={cn('space-y-9 w-full', className)}
    {...props}
  />
))
AccordionStepper.displayName = 'AccordionStepper'

const accordionStepperItemVariants = cva('w-full rounded-sm border', {
  variants: {
    variant: {
      default: 'border-system-border-primary',
      active: 'border-system-primary-purple',
      disabled:
        'pointer-events-none cursor-not-allowed border-system-grey-strong',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})
type AccordionStepperItemVariants = VariantProps<
  typeof accordionStepperItemVariants
>
const AccordionStepperItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> &
    AccordionStepperItemVariants
>(({ className, variant, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(accordionStepperItemVariants({ variant, className }))}
    {...props}
  />
))
AccordionStepperItem.displayName = 'AccordionStepperItem'

const accordionStepperStepContainerVariants = cva(
  'relative flex w-full gap-4 before:absolute before:left-4 before:top-8 before:z-10 before:h-[calc(100%+4px)] before:w-[2px] last:before:hidden',
  {
    variants: {
      variant: {
        default: 'before:bg-system-border-primary',
        active: 'before:bg-system-primary-purple',
        completed: 'before:bg-green-500',
        disabled:
          'pointer-events-none cursor-not-allowed before:bg-system-grey-strong',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)
const AccordionStepperStepContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof accordionStepperStepContainerVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      accordionStepperStepContainerVariants({ className, variant }),
    )}
    {...props}
  />
))
AccordionStepperStepContainer.displayName = 'AccordionStepperStepContainer'

const accordionStepperStepVariants = cva(
  'flex size-8 shrink-0 items-center justify-center rounded-full text-sm text-system-text-primary',
  {
    variants: {
      variant: {
        default:
          'border-2 border-system-border-primary bg-white text-system-text-primary',
        active: 'bg-system-primary-purple text-white',
        completed: 'bg-green-500 text-white',
        disabled:
          'pointer-events-none cursor-not-allowed border-0 bg-system-grey-strong text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)
const AccordionStepperStep = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof accordionStepperStepVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(accordionStepperStepVariants({ className, variant }))}
    {...props}
  />
))
AccordionStepperStep.displayName = 'AccordionStepperStep'

const accordionStepperTriggerVariants = cva(
  'flex w-full flex-1 items-center justify-between px-4 py-2 text-base font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
  {
    variants: {
      variant: {
        default: 'text-system-content-american-gray',
        active:
          'border-b border-system-primary-purple text-system-text-primary',
        disabled:
          'pointer-events-none cursor-not-allowed text-system-grey-strong',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)
type AccordionStepperTriggerVariants = VariantProps<
  typeof accordionStepperTriggerVariants
>
const AccordionStepperTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> &
    AccordionStepperTriggerVariants
>(({ className, variant, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        accordionStepperTriggerVariants({
          variant,
          className,
        }),
      )}
      {...props}
    >
      {children}
      <ChevronDown className="size-4 shrink-0 text-system-primary-icon transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
AccordionStepperTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionStepperContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn('px-5 py-4 bg-system-background-grey', className)}>
      {children}
    </div>
  </AccordionPrimitive.Content>
))

AccordionStepperContent.displayName = AccordionPrimitive.Content.displayName

export {
  AccordionStepper,
  AccordionStepperItem,
  AccordionStepperStepContainer,
  AccordionStepperStep,
  AccordionStepperTrigger,
  AccordionStepperContent,
}
